/**
 * The M7 rail's conversation.
 *
 * Messages-style: one time label per cluster, your words in a plane on the
 * right, the agent's words open on the rail. Every agent turn opens on the M7
 * mark; its thinking folds into one line, its tools into quiet rows, and the
 * model that actually wrote it sits under the reply. A card the agent put up
 * folds down to your pick once answered, so the echo of your answer is dropped.
 *
 * Private chats only. The shared-thread rules (authors, seen marks, cards that
 * stay open across messages) live in the Workshop's ledger transcript.
 */
import { Fragment, type MutableRefObject, type ReactNode, type RefObject } from "react";
import type { ChatMessage, ChatModel } from "@/types/chat.type";
import type { AgentFocus } from "@/types/agentFocus.type";
import type { ChatAttachmentItem } from "@/app/routes/dashboard/api/useSessionAttachments";
import type { StreamingBlock } from "@/app/routes/dashboard/components/streamingTurnBlocks";
import { splitTurnRounds } from "@/app/routes/dashboard/components/turnRounds";
import { getMessageThinking } from "@/app/routes/dashboard/components/ThoughtSummary";
import { getMessageUsage } from "@/app/routes/dashboard/components/MessageUsageBadge";
import { parseAskUserQuestions } from "@/app/routes/dashboard/components/askUserQuestion";
import { parseVerifyRequest, type VerifyRequest } from "@/app/routes/dashboard/components/verifyWithUser";
import {
  parsePlanMonthRequest,
  readPlanMonthAnswer,
  type PlanMonthAnswer,
  type PlanMonthRequest,
} from "@/app/routes/dashboard/components/planMonth";
import { findPendingInteraction } from "@/app/routes/dashboard/components/agentInteractions";
import { attachedVideosOf } from "@/app/routes/dashboard/components/attachedVideos";
import {
  isCustomRenderedTool,
  mapPersistedTool,
  mapStreamingTool,
  type RailToolItem,
} from "@/app/routes/dashboard/components/railTools";
import { focusChanged, focusLabel } from "../focusLabel";
import {
  ClusterTime,
  HumanBubble,
  MarkRow,
  Notice,
  ReplyFoot,
  ReplyText,
  StreamError,
  StreamingText,
  ThinkingRow,
  ToolRows,
  useTapToReveal,
} from "./RailParts";
import { AgentVideos, LinkEmbeds, SentFiles } from "./RailMedia";
import { standaloneLinks } from "./railLinks";
import { useSendFlight, type SendFlight } from "./sendFlight";
import {
  PlanMonthAnswered,
  QuestionAnswered,
  RailPlanMonthCard,
  RailQuestionCard,
  RailVerifyCard,
  VerifyAnswered,
} from "./RailCards";
import {
  answeringModel,
  placeAttachments,
  readQuestionAnswer,
  type AttachmentClaims,
  readVerifyAnswer,
  startsCluster,
  withoutSlideContext,
  type CardAnswer,
  type VerifyAnswer,
} from "./railFormat";

export type { SendFlight };

type Props = {
  messages: ChatMessage[];
  /** Every file in the chat; each sits above the message it went out with. */
  attachments: ChatAttachmentItem[];
  /** Videos the agent attached from links, by document id. */
  linkedAttachments: Map<string, ChatAttachmentItem>;
  /** Files sent from this tab, by the message they went out with. */
  attachmentClaims?: AttachmentClaims;
  isLoading: boolean;
  isDeleting: boolean;
  isStreaming: boolean;
  streamingBlocks: StreamingBlock[];
  streamError: string | null;
  /** The picker's list, to name the model that answered. */
  models: ChatModel[];
  emptyState: ReactNode;
  /** Sends a card's answer. Absent, every card renders read-only. */
  onRespond?: (reply: string) => void;
  respondDisabled: boolean;
  onRetryAttachment?: (documentId: string) => void;
  scrollRef: RefObject<HTMLDivElement | null>;
  onScrolledChange: (scrolled: boolean) => void;
  flightRef: MutableRefObject<SendFlight | null>;
  ariaLabel?: string;
  /** A page's own question, after everything else (and in place of the blank state). */
  localTurn?: ReactNode;
};

type Entry = { key: string; createdAt: string; timed: boolean; node: ReactNode };

type CardAnswers = {
  questions: Map<string, CardAnswer | null>;
  verify: Map<string, VerifyAnswer | null>;
  plan: Map<string, PlanMonthAnswer | null>;
  /** User messages that only echo a card's answer. The card shows the pick. */
  echoes: Set<string>;
};

function readCardAnswers(messages: ChatMessage[], pendingId: string | null): CardAnswers {
  const questions = new Map<string, CardAnswer | null>();
  const verify = new Map<string, VerifyAnswer | null>();
  const plan = new Map<string, PlanMonthAnswer | null>();
  const echoes = new Set<string>();
  messages.forEach((msg, i) => {
    if (msg.role !== "assistant" || msg.id === pendingId) return;
    const events = msg.toolEvents ?? [];
    const asked = events.flatMap((event) => parseAskUserQuestions(event) ?? []);
    const request = events.map(parseVerifyRequest).find((r): r is VerifyRequest => r !== null);
    const planRequest = events.map(parsePlanMonthRequest).find((r): r is PlanMonthRequest => r !== null);
    if (asked.length === 0 && !request && !planRequest) return;
    const next = messages[i + 1];
    const reply = next?.role === "user" ? next : null;
    if (planRequest && asked.length === 0 && !request) {
      const answer = reply ? readPlanMonthAnswer(planRequest, reply.content) : null;
      plan.set(msg.id, answer);
      if (answer && reply) echoes.add(reply.id);
    } else if (asked.length > 0) {
      const answer = reply ? readQuestionAnswer(asked, reply.content) : null;
      questions.set(msg.id, answer);
      if (answer && reply) echoes.add(reply.id);
    } else if (request) {
      const answer = reply ? readVerifyAnswer(request.items, reply.content) : null;
      verify.set(msg.id, answer);
      if (answer && reply) echoes.add(reply.id);
    }
  });
  return { questions, verify, plan, echoes };
}

function AssistantTurn({
  msg,
  models,
  linked,
  pending,
  answers,
  onRespond,
  respondDisabled,
}: {
  msg: ChatMessage;
  models: ChatModel[];
  linked: Map<string, ChatAttachmentItem>;
  pending: boolean;
  answers: CardAnswers;
  onRespond?: (reply: string) => void;
  respondDisabled: boolean;
}) {
  const tap = useTapToReveal();
  const thinking = getMessageThinking(msg.metadata);
  const events = msg.toolEvents ?? [];
  const rounds = splitTurnRounds(msg.content, thinking, events);
  const asked = events.flatMap((event) => parseAskUserQuestions(event) ?? []);
  const request = events.map(parseVerifyRequest).find((r): r is VerifyRequest => r !== null);
  const planRequest = events.map(parsePlanMonthRequest).find((r): r is PlanMonthRequest => r !== null);
  const openCard = pending && onRespond ? onRespond : undefined;
  const hasText = rounds.some((round) => Boolean(round.text));

  return (
    <div className="m7r-turn" data-show={tap.shown || undefined} onClick={tap.onClick}>
      {rounds[0]?.thinking ? null : <MarkRow />}
      {rounds.map((round, r) => {
        const tools = round.tools
          .filter((event) => !isCustomRenderedTool(event.toolName))
          .map(mapPersistedTool);
        const videos = round.tools.flatMap((event) => attachedVideosOf(event));
        // A link the agent put on its own line shows as its embed.
        const { text, links } = round.text ? standaloneLinks(round.text) : { text: "", links: [] };
        return (
          <Fragment key={`${msg.id}-${r}`}>
            {round.thinking ? <ThinkingRow thinking={round.thinking} withMark={r === 0} /> : null}
            {text ? <ReplyText content={text} /> : null}
            <LinkEmbeds links={links} side="agent" />
            <ToolRows tools={tools} />
            <AgentVideos videos={videos} linked={linked} />
          </Fragment>
        );
      })}
      {hasText ? (
        <ReplyFoot
          text={msg.content}
          model={answeringModel(msg.metadata, models)}
          modelId={getMessageUsage(msg.metadata)?.model ?? null}
          createdAt={msg.createdAt}
          usage={getMessageUsage(msg.metadata)}
        />
      ) : null}
      {asked.length > 0 ? (
        openCard ? (
          <RailQuestionCard questions={asked} onSubmit={openCard} disabled={respondDisabled} />
        ) : (
          <QuestionAnswered questions={asked} answer={answers.questions.get(msg.id) ?? null} />
        )
      ) : null}
      {request && asked.length === 0 ? (
        openCard ? (
          <RailVerifyCard request={request} onSubmit={openCard} disabled={respondDisabled} />
        ) : (
          <VerifyAnswered request={request} answer={answers.verify.get(msg.id) ?? null} />
        )
      ) : null}
      {planRequest && asked.length === 0 && !request ? (
        openCard ? (
          <RailPlanMonthCard request={planRequest} onSubmit={openCard} disabled={respondDisabled} />
        ) : (
          <PlanMonthAnswered request={planRequest} answer={answers.plan.get(msg.id) ?? null} />
        )
      ) : null}
      {msg.status === "aborted" ? <p className="m7r-stopped">Stopped by you</p> : null}
    </div>
  );
}

/** The reply being written right now, in the order it streams. */
function StreamingTurn({ blocks }: { blocks: StreamingBlock[] }) {
  const nodes: ReactNode[] = [];
  let tools: RailToolItem[] = [];
  const flushTools = () => {
    if (tools.length === 0) return;
    nodes.push(<ToolRows key={`tools-${nodes.length}`} tools={tools} />);
    tools = [];
  };

  blocks.forEach((block, i) => {
    if (block.kind === "thinking") {
      flushTools();
      nodes.push(
        <ThinkingRow
          key={block.block.id}
          thinking={{ text: block.block.text, durationMs: block.block.durationMs }}
          withMark={i === 0}
          live={block.block.isLive}
        />,
      );
    } else if (block.kind === "tool") {
      // What the agent put up shows the moment it lands, read-only: the turn
      // is still open, and answering means sending the next message.
      const asked = parseAskUserQuestions(block.event);
      if (asked) {
        flushTools();
        nodes.push(<RailQuestionCard key={block.event.toolCallId} questions={asked} disabled />);
        return;
      }
      const request = parseVerifyRequest(block.event);
      if (request) {
        flushTools();
        nodes.push(<RailVerifyCard key={block.event.toolCallId} request={request} disabled />);
        return;
      }
      const planRequest = parsePlanMonthRequest(block.event);
      if (planRequest) {
        flushTools();
        nodes.push(<RailPlanMonthCard key={block.event.toolCallId} request={planRequest} disabled />);
        return;
      }
      if (isCustomRenderedTool(block.event.toolName)) return;
      tools.push(mapStreamingTool(block.event));
    } else if (block.block.content.trim()) {
      flushTools();
      nodes.push(
        <StreamingText key={block.block.id} content={block.block.content} live={block.block.isLive} />,
      );
    }
  });
  flushTools();

  const last = blocks[blocks.length - 1];
  const writing =
    (last?.kind === "text" && last.block.isLive) ||
    (last?.kind === "thinking" && last.block.isLive) ||
    (last?.kind === "tool" && last.event.status === "running");
  const opensOnThinking = blocks[0]?.kind === "thinking";

  return (
    <div className="m7r-turn" data-live>
      {opensOnThinking ? null : blocks.length === 0 ? (
        <ThinkingRow thinking={null} withMark live />
      ) : (
        <MarkRow />
      )}
      {nodes}
      {blocks.length > 0 && !writing ? <ThinkingRow thinking={null} withMark={false} live /> : null}
    </div>
  );
}

export default function RailConversation({
  messages,
  attachments,
  linkedAttachments,
  attachmentClaims,
  isLoading,
  isDeleting,
  isStreaming,
  streamingBlocks,
  streamError,
  models,
  emptyState,
  onRespond,
  respondDisabled,
  onRetryAttachment,
  scrollRef,
  onScrolledChange,
  flightRef,
  ariaLabel = "Conversation with Creo",
  localTurn = null,
}: Props) {
  const pendingId = isStreaming ? null : (findPendingInteraction(messages)?.messageId ?? null);
  const answers = readCardAnswers(messages, pendingId);

  useSendFlight({
    flightRef,
    scrollRef,
    messages,
    textOf: (message) => withoutSlideContext(message.content),
  });

  // Files attached since your last message wait in the composer, not here.
  const { byMessage } = placeAttachments(messages, attachments, attachmentClaims);
  const showEmpty = messages.length === 0 && !isStreaming && streamingBlocks.length === 0;

  const entries: Entry[] = [];
  messages.forEach((msg, i) => {
    const previous = messages[i - 1];
    const from = previous?.metadata?.focus as AgentFocus | undefined;
    const to = msg.metadata?.focus as AgentFocus | undefined;
    if (previous && to && focusChanged(from, to)) {
      entries.push({
        key: `focus-${msg.id}`,
        createdAt: msg.createdAt,
        timed: false,
        node: <Notice>Moved to {focusLabel(to, true)}</Notice>,
      });
    }
    if (msg.role === "user") {
      const files = byMessage.get(msg.id) ?? [];
      if (answers.echoes.has(msg.id)) {
        // The card shows the answer; files sent with it still need a place.
        if (files.length > 0) {
          entries.push({
            key: `files-${msg.id}`,
            createdAt: msg.createdAt,
            timed: true,
            node: (
              <div className="m7r-you">
                <SentFiles items={files} onRetry={onRetryAttachment} />
              </div>
            ),
          });
        }
        return;
      }
      entries.push({
        key: msg.id,
        createdAt: msg.createdAt,
        timed: true,
        node: (
          <HumanBubble
            id={msg.id}
            text={msg.content}
            createdAt={msg.createdAt}
            files={files}
            onRetryFile={onRetryAttachment}
          />
        ),
      });
      return;
    }
    entries.push({
      key: msg.id,
      createdAt: msg.createdAt,
      timed: true,
      node: (
        <AssistantTurn
          msg={msg}
          models={models}
          linked={linkedAttachments}
          pending={msg.id === pendingId}
          answers={answers}
          onRespond={onRespond}
          respondDisabled={respondDisabled}
        />
      ),
    });
  });
  // Stable: a focus notice sorts just ahead of the message it introduces.
  entries.sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  // A page-change notice belongs to the message after it, so it sits under that
  // message's time label rather than above it.
  const rendered: ReactNode[] = [];
  let lastTimed: string | null = null;
  let held: Entry[] = [];
  for (const entry of entries) {
    if (!entry.timed) {
      held.push(entry);
      continue;
    }
    if (startsCluster(lastTimed, entry.createdAt)) {
      rendered.push(<ClusterTime key={`time-${entry.key}`} iso={entry.createdAt} />);
    }
    lastTimed = entry.createdAt;
    for (const notice of held) rendered.push(<Fragment key={notice.key}>{notice.node}</Fragment>);
    held = [];
    rendered.push(<Fragment key={entry.key}>{entry.node}</Fragment>);
  }
  for (const notice of held) rendered.push(<Fragment key={notice.key}>{notice.node}</Fragment>);

  return (
    <div
      ref={scrollRef}
      className="m7r-log"
      role="log"
      aria-live="polite"
      aria-label={ariaLabel}
      aria-busy={isLoading || isStreaming || undefined}
      tabIndex={0}
      onScroll={(e) => onScrolledChange(e.currentTarget.scrollTop > 4)}
    >
      {isDeleting ? (
        <p className="m7r-skeleton-status" role="status">
          Deleting this chat
        </p>
      ) : isLoading ? (
        <div className="m7r-skeleton" aria-label="Loading the chat">
          <i />
          <i />
          <i />
          <i />
        </div>
      ) : showEmpty ? (
        (localTurn ?? emptyState)
      ) : (
        <>
          {rendered}
          {isStreaming ? <StreamingTurn blocks={streamingBlocks} /> : null}
          {streamError ? <StreamError message={streamError} /> : null}
          {localTurn}
        </>
      )}
    </div>
  );
}
